import { useState } from 'react';
import { NextSeo } from 'next-seo';
import Image from 'next/image';
import router from 'next/router';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useWindowSize } from 'react-use';

import { useTranslation } from '@hooks';
import { useAccount } from '@context/AccountContext';
import { AccountServices } from '@services';
import { AccountPagesLayout, SearchBar } from '@components/account';
import { NullProductCard } from '@components/ui';
import { ProductCard } from '@components/listing';

import { LISTINGTYPES } from '../[category]';

const Favorites = () => {
    const { t, locale } = useTranslation();
    const { currentUser } = useAccount();
    const { width } = useWindowSize();
    const [searchQuery, setSearchQuery] = useState('');

    const searchFavorites = (query) => {
        setSearchQuery(query);
    };

    const { data, isLoading } = useQuery({
        queryKey: ['getFavorites'],
        queryFn: () => AccountServices.getFavorites(currentUser),
        enabled: !!currentUser,
    });

    const favorites = data?.data?.data || [];

    const searchResult =
        favorites?.filter((item) =>
            item.name[locale].toLowerCase().includes(searchQuery.toLowerCase()),
        ) || [];

    const hasFavoriteProduct = searchResult.length > 0;

    return (
        <>
            <NextSeo
                title={`Fervente | ${t('favorites')}`}
                description={t('accountMetaDesc')}
            />

            <AccountPagesLayout
                showCartButton
                activeMenu="favorites"
                menuItemCount={favorites?.length}
                productCount={
                    favorites?.length > 0 &&
                    `${favorites?.length} ${t('product')}`
                }
                title={t('favorites')}
            >
                {isLoading ? (
                    <p>Loading...</p>
                ) : (
                    <FavoritesWrapper>
                        <FavoritesHeader
                            hasFavoriteProduct={hasFavoriteProduct}
                        >
                            <SearchBar
                                placeholder={t('searchInFavorites')}
                                search={searchFavorites}
                            />
                        </FavoritesHeader>
                        {hasFavoriteProduct ? (
                            <CardWrapper>
                                {searchResult.map((item, i) => (
                                    <div key={`favorite-${item.id}`}>
                                        {width > 1024 ? (
                                            <DesktopItemWrapper>
                                                <Image
                                                    src={
                                                        item.images[0].urls
                                                            .medium
                                                    }
                                                    role="presentation"
                                                    alt={item?.name}
                                                    onClick={() =>
                                                        router.push(
                                                            `/product/${item.slug[locale]}`,
                                                        )
                                                    }
                                                    fill
                                                    data-testid="favorite-card-image"
                                                />
                                            </DesktopItemWrapper>
                                        ) : (
                                            <ProductCard
                                                product={item}
                                                priority={2}
                                                border={i < 2}
                                                listingType={
                                                    LISTINGTYPES.double
                                                }
                                            />
                                        )}
                                    </div>
                                ))}
                            </CardWrapper>
                        ) : favorites?.data?.length < 1 ? (
                            <NullProductCard
                                text={t('noFavoriteProduct')}
                                photo="https://cloud.keikei.com/cdn/svgs/fav.png?2"
                                buttonTitle={t('cartContinueShopping')}
                            />
                        ) : (
                            <NullProductCard
                                text={t('noSearchResult')}
                                photo="https://cloud.keikei.com/cdn/svgs/search.png?reload"
                                buttonTitle={t('searchAgain')}
                            />
                        )}
                    </FavoritesWrapper>
                )}
            </AccountPagesLayout>
        </>
    );
};

const FavoritesWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
`;
const CardWrapper = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 1fr;
    border-color: ${({ theme }) => theme.colors.primary};
    border-style: solid;
    border-width: 1px 0 0 1px;

    @media (max-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        margin-top: 10px;
    }
`;
const DesktopItemWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 37.63vw;
    border-color: ${({ theme }) => theme.colors.primary};
    border-style: solid;
    border-width: 0 1px 1px 0;

    img {
        width: 100%;
        object-fit: cover;
        cursor: pointer;
    }
`;
const FavoritesHeader = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 22px;

    @media (max-width: 1024px) {
        margin-bottom: 0;
        width: 100%;
        background: ${({ theme }) => theme.colors.secondary};
        padding: 20px;
        z-index: 3;

        p {
            display: none;
        }

        div {
            width: 100%;
        }
    }
`;
export default Favorites;
